import {
  MouseEvent as ReactMouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

interface IProps {
  // Persist key so different panels can each remember their own width.
  storageKey: string
  // Fraction of the viewport used the first time, before the user drags.
  defaultFraction?: number
  // Absolute first-run width. Wins over defaultFraction — for panels whose design
  // width is a fixed number of px rather than a share of the screen.
  defaultWidth?: number
  // Hard pixel floor so the panel can't be dragged uselessly narrow.
  minWidth?: number
  // Fraction of the viewport the panel may not exceed (keeps the table usable).
  maxFraction?: number
  // Absolute pixel ceiling, applied on top of maxFraction. For panels that should
  // stop growing on a big monitor instead of scaling with it.
  maxWidth?: number
  // The element whose width the panel actually shares — its flex row. When it is
  // set and measurable it *replaces* the viewport arithmetic below, because that
  // arithmetic is a model of the page and a model is always a little wrong: it can't
  // see MainLayout's in-flow nav rail (64px collapsed, 232px pinned), a page gutter,
  // or a sibling panel outside the row. A measured row has all of that taken out of
  // it already, so nothing has to be mirrored in JS to stay correct.
  containerRef?: RefObject<HTMLElement | null>
  // Floor to preserve for the column the panel shares its row with. The ceiling
  // becomes whatever is left after that floor, so the panel gives up space before
  // its neighbour becomes unusable. Expressed directly rather than as a fraction
  // because "the conversation stays readable" is the actual requirement, and the
  // fraction that satisfies it differs at every window width.
  siblingMinWidth?: number
  // Which edge of the row the panel is pinned to, i.e. which way the drag grows it.
  // Only the cursor arithmetic differs; everything downstream of the resulting width
  // — clamping, persistence, the re-clamp observers — is shared.
  anchor?: 'left' | 'right'
}

const DEFAULT_FRACTION = 0.3
const DEFAULT_MIN_WIDTH = 360
const DEFAULT_MAX_FRACTION = 0.7

// A right-anchored panel's width grows as the drag moves left; a left-anchored one
// (the messages conversation list) grows as it moves right. All widths are stored in
// pixels (localStorage) and re-clamped against the space the panel currently has —
// the measured row when the caller passes a containerRef, the viewport otherwise —
// so a value saved on a wide screen can't overflow a narrow one.
const useResizablePanelWidth = ({
  storageKey,
  defaultFraction = DEFAULT_FRACTION,
  defaultWidth,
  minWidth = DEFAULT_MIN_WIDTH,
  maxFraction = DEFAULT_MAX_FRACTION,
  maxWidth,
  containerRef,
  siblingMinWidth,
  anchor = 'right',
}: IProps) => {
  // The panel's right edge isn't always the viewport edge. Read the reserved
  // offset off the handle at drag start (CSS owns the value, including its
  // breakpoints) so the drag tracks the cursor instead of lagging by that strip.
  const rightOffsetRef = useRef(0)

  // The left-anchored mirror of the above: the panel's left edge is the row's left
  // edge, which the nav rail and any page gutter have already pushed off the viewport
  // edge. Read from the row at drag start rather than modelled in JS, for the same
  // reason the ceiling is measured — see containerRef.
  const leftOffsetRef = useRef(0)

  // What the user asked for, independent of whether it fits today. Only a drag, a
  // stored value or `adjustWidth` moves this; a narrow viewport never does. That
  // separation is the point: the hook stays mounted below the breakpoints that hide
  // the handle, so folding "doesn't fit right now" into the remembered width let a
  // phone-sized viewport overwrite a 500px desktop width with the 240px floor, which
  // nothing then restored once the row was wide enough for it again.
  const clampToBounds = useCallback(
    (width: number) => Math.min(Math.max(width, minWidth), maxWidth ?? Number.POSITIVE_INFINITY),
    [minWidth, maxWidth],
  )

  // The space the panel is dividing up. The measured row wins where there is one: it
  // already excludes the nav rail, any list beside the content and any sibling panel,
  // none of which the viewport sum can see. Zero means "not laid out yet, or laid out
  // to nothing" (the first render, or hidden behind a media query), and then the
  // viewport is the only number available — corrected before paint by the layout
  // effect below. rightOffset is any strip already reserved to the panel's right
  // (e.g. an open push sidebar).
  const readAvailableWidth = useCallback(
    (rightOffset = 0) => {
      const measured = containerRef?.current?.clientWidth ?? 0
      return measured > 0 ? measured : window.innerWidth - rightOffset
    },
    [containerRef],
  )

  // How much of the requested width the row can actually seat right now.
  const fitToRow = useCallback(
    (width: number, available: number) => {
      const fractionMax = Math.max(Math.round(available * maxFraction), minWidth)
      const fixedMax = maxWidth ? Math.min(maxWidth, fractionMax) : fractionMax
      // minWidth still wins if the row is too narrow to honour both floors — the
      // panel can't be dragged to nothing. Layout resolves that case symmetrically:
      // below the width at which both floors fit, each column keeps its share of the
      // row instead of one of them being starved (see _panels.scss / _main.scss).
      const max = siblingMinWidth
        ? Math.min(fixedMax, Math.max(minWidth, available - siblingMinWidth))
        : fixedMax
      return Math.min(Math.max(width, minWidth), max)
    },
    [minWidth, maxFraction, maxWidth, siblingMinWidth],
  )

  const getInitialRequestedWidth = useCallback((): number => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = parseInt(stored, 10)
        if (!Number.isNaN(parsed)) {
          return clampToBounds(parsed)
        }
      }
    } catch {
      // localStorage unavailable (private mode / disabled) — fall through.
    }
    return clampToBounds(defaultWidth ?? Math.round(window.innerWidth * defaultFraction))
  }, [storageKey, defaultFraction, defaultWidth, clampToBounds])

  const [requestedWidth, setRequestedWidth] = useState<number>(getInitialRequestedWidth)
  const [availableWidth, setAvailableWidth] = useState<number>(() => readAvailableWidth())
  const [isResizing, setIsResizing] = useState(false)

  // Derived rather than stored, so the width the panel renders at is always the
  // requested width measured against the room there is now — and widens back on its
  // own when the room returns.
  const width = useMemo(
    () => fitToRow(requestedWidth, availableWidth),
    [fitToRow, requestedWidth, availableWidth],
  )

  // Kept current so the mouseup persist reads the latest width without
  // re-subscribing the listeners on every pixel of movement.
  const widthRef = useRef(width)
  widthRef.current = width

  const handleResizeStart = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault()
      if (anchor === 'left') {
        leftOffsetRef.current = containerRef?.current?.getBoundingClientRect().left ?? 0
      } else {
        const offset = getComputedStyle(event.currentTarget).getPropertyValue(
          '--panel-right-offset',
        )
        rightOffsetRef.current = parseFloat(offset) || 0
      }
      setIsResizing(true)
    },
    [anchor, containerRef],
  )

  useEffect(() => {
    if (!isResizing) {
      return
    }

    // Fitted to the row as the cursor moves, not merely bounded: a drag that runs
    // past the ceiling has to give the width straight back on the way in, or the
    // panel would sit still until the cursor caught up with it again.
    const widthAt = (clientX: number) => {
      if (anchor === 'left') {
        return fitToRow(clientX - leftOffsetRef.current, readAvailableWidth())
      }
      const rightOffset = rightOffsetRef.current
      return fitToRow(window.innerWidth - rightOffset - clientX, readAvailableWidth(rightOffset))
    }

    // One width per frame: each one reflows everything sharing the row, and a mouse
    // reports far more often than the screen paints.
    let pendingX: number | null = null
    let frameId = 0
    const applyPending = () => {
      frameId = 0
      if (pendingX === null) return null
      const next = widthAt(pendingX)
      pendingX = null
      setRequestedWidth(next)
      return next
    }

    const handleMouseMove = (event: MouseEvent) => {
      pendingX = event.clientX
      if (!frameId) frameId = requestAnimationFrame(applyPending)
    }

    const handleMouseUp = () => {
      cancelAnimationFrame(frameId)
      const finalWidth = applyPending() ?? widthRef.current
      setIsResizing(false)
      try {
        localStorage.setItem(storageKey, String(finalWidth))
      } catch {
        // Persisting is best-effort — the in-memory width still applies.
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    // Suppress text selection / show the resize cursor for the whole drag.
    const previousUserSelect = document.body.style.userSelect
    const previousCursor = document.body.style.cursor
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'

    return () => {
      cancelAnimationFrame(frameId)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = previousUserSelect
      document.body.style.cursor = previousCursor
    }
  }, [isResizing, fitToRow, readAvailableWidth, storageKey, anchor])

  // Re-measure the row the panel is actually in, and keep doing it whenever that row
  // changes width. Collapsing a sidebar hands its strip back, pinning the nav rail
  // takes 168px away, opening a panel outside the row narrows it — none of those fire
  // a `resize` event, and none of them are visible to the viewport sum.
  //
  // A layout effect, so the correction lands before the browser paints: the first
  // render has no element to measure and can only use the viewport, and this is what
  // stops that guess ever reaching the screen.
  useLayoutEffect(() => {
    const sync = () => setAvailableWidth(readAvailableWidth(rightOffsetRef.current))
    sync()

    const container = containerRef?.current
    if (!container || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(sync)
    observer.observe(container)
    return () => observer.disconnect()
  }, [containerRef, readAvailableWidth])

  // Re-measure when the viewport changes so the panel never exceeds maxFraction.
  useEffect(() => {
    const handleWindowResize = () => setAvailableWidth(readAvailableWidth(rightOffsetRef.current))
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [readAvailableWidth])

  // Set the width directly, clamped and persisted — for resize affordances that
  // aren't a pointer drag (keyboard nudges on the handle, presets).
  const adjustWidth = useCallback(
    (nextWidth: number) => {
      const clamped = fitToRow(nextWidth, readAvailableWidth(rightOffsetRef.current))
      setRequestedWidth(clamped)
      try {
        localStorage.setItem(storageKey, String(clamped))
      } catch {
        // Persisting is best-effort — the in-memory width still applies.
      }
      return clamped
    },
    [fitToRow, readAvailableWidth, storageKey],
  )

  return { width, isResizing, handleResizeStart, adjustWidth }
}

export default useResizablePanelWidth
